/**
 * Running a community, at `/{slug}/admin`.
 *
 * Panels because there are separate jobs, and they are genuinely different:
 * WHO IS IN IT is curation, HOW IT IS SORTED is the community's own
 * vocabulary (its game and its groupings, ADR 0052), WHO RUNS IT is
 * authority, and WHO IS BLOCKED is moderation. Collapsing them into one list of "people" would put
 * a streamer nobody has met beside an account somebody had to silence.
 *
 * The page does not re-implement the server's rules. Appointing admins is
 * owner-only and the rest is moderator-level; asking and showing what comes
 * back is the whole of the client's part, because a second copy of an
 * authority check is a second thing to get wrong and the one that matters is
 * not this one.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type {
  RoomAdmin as RoomAdminShape, RoomGame, RoomGroupings as RoomGroupingsShape, RoomMute,
} from '@tomo/contracts';
import { ROOM_GAMES_MAX } from '@tomo/contracts';
import { ROOM_ROSTER_MAX } from '@tomo/contracts';
import { Button, Empty, Failed, Spinner } from '../components/primitives.jsx';
import { auth, communities, communityAdmin } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import {
  addLogin, addLogins, describeBulk, hasChanges, looksLikeList, removeLogin,
} from '../lib/communityMembers.js';
import {
  addGrouping, addPeople, chipsFor, draftFrom, editGrouping, groupingsBody, groupingsChanged,
  moveGrouping, removeGrouping, tickState, toggleTick, unsavableBecause, type GroupingDraft,
} from '../lib/communityGroupings.js';
import type { Async } from '../useAsync.js';
import { ApiError } from '../lib/api.js';
import { SiteFrame } from '../components/SiteFrame.jsx';
import { useViewer } from '../lib/viewer.js';

export function CommunityAdmin({ slug }: { readonly slug: string }): ReactNode {
  const [room, retryRoom] = useAsync((signal) => communities.get(slug, signal), [slug]);
  const [roster] = useAsync((signal) => communities.roster(slug, signal), [slug]);
  const viewer = useViewer();
  /**
   * Who runs it — asked for, but NOT what decides whether this page opens.
   *
   * It used to be: the admins endpoint is owner-only, and its 403 was read as
   * "not yours to run". So an EDITOR — somebody the owner appointed to run
   * the community, with every edit right the API grants — opened the admin
   * page and was told it was not theirs. The page now asks the room what the
   * caller may do (`your_role`), and only the Admins panel, which is the
   * owner's alone, depends on this answer.
   */
  const [admins, retryAdmins] = useAsync((signal) => communityAdmin.list(slug, signal), [slug]);
  const [groupings, retryGroupings] = useAsync((signal) => communityAdmin.groupings(slug, signal), [slug]);

  if (room.state === 'loading') return <Spinner label="Loading…" />;
  if (room.state === 'failed') {
    return (
      <Frame slug={slug}>
        <Failed
          message={room.error.status === 404
            ? 'No such community.'
            : 'Could not load this community.'}
          detail={room.error.message}
          onRetry={retryRoom}
        />
      </Frame>
    );
  }

  // Signed in or not is only known once the session has been asked about.
  if (viewer === undefined) return <Spinner label="Loading…" />;

  const signIn = (): void => {
    void auth.start(window.location.pathname + window.location.search).then(
      (url) => { window.location.assign(url); },
      () => { /* A login that cannot start leaves the page exactly as it was. */ },
    );
  };

  if (viewer === null) {
    return (
      <Frame slug={slug} name={room.value.name}>
        <Panel title="Sign in" hint={`Running ${room.value.name} needs an account.`}>
          {/*
            The same call the account menu makes. This was a link to
            `/v1/auth/twitch/start`, a route that has never existed: sign-in
            is a POST that answers with Twitch's address, so a link cannot
            start it and every signed-out admin got a 404.
          */}
          <Button onClick={signIn}>Sign in with Twitch</Button>
        </Panel>
      </Frame>
    );
  }

  /*
   * `your_role` is the server's answer for THIS caller: owner (a platform
   * admin is reported as one), editor, or absent. The page draws controls
   * from it; every route still checks for itself.
   */
  const role = room.value.your_role ?? null;
  if (role === null) {
    return (
      <Frame slug={slug} name={room.value.name}>
        <Panel title="Not yours to run" hint="Only the people who run this community can change it: its owner and the editors they appoint.">
          <a
            href={`/${encodeURIComponent(slug)}`}
            className="text-sm text-brand-300 hover:text-brand-400"
          >
            Back to {room.value.name}
          </a>
        </Panel>
      </Frame>
    );
  }

  const fromFeed = room.value.roster_feed ?? null;
  const rosterLogins = roster.state === 'ready' ? roster.value.data.map((m) => m.login) : [];

  return (
    <Frame slug={slug} name={room.value.name}>
      <About slug={slug} saved={room.value.description ?? ''} />
      <Visibility slug={slug} saved={room.value.visibility ?? 'public'} />
      <Game slug={slug} saved={room.value.games ?? []} />
      <Curation
        slug={slug}
        saved={room.value.twitch_logins ?? []}
        fromFeed={fromFeed}
        feedLogins={rosterLogins}
        groupings={groupings}
        retryGroupings={retryGroupings}
      />
      {role === 'owner' ? (
        <Admins slug={slug} rows={admins.state === 'ready' ? admins.value : []} onChanged={retryAdmins} />
      ) : (
        <Panel title="Admins" hint="Only the owner can change who runs this community.">
          <p className="text-sm text-ink-400">You are an editor here.</p>
        </Panel>
      )}
      <Blocks slug={slug} />
    </Frame>
  );
}

function Frame({
  slug, name, children,
}: {
  readonly slug: string;
  readonly name?: string;
  readonly children: ReactNode;
}): ReactNode {
  // Fetched HERE rather than in the page: `Frame` is rendered from five
  // different branches (loading, 401, 403, not-found, the page itself) and
  // threading a viewer through each one is five chances to forget.
  const viewer = useViewer();
  return (
    <SiteFrame
      viewer={viewer}
      trail={[{ label: name ?? slug, href: `/${encodeURIComponent(slug)}` }, { label: 'admin' }]}
    >
      {/* A form's measure, inside the one frame — see SiteFrame. */}
      <div className="max-w-3xl space-y-8">{children}</div>
    </SiteFrame>
  );
}

function Panel({
  title, hint, children,
}: {
  readonly title: string;
  readonly hint: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className="bg-ink-900 p-4">
      <h2 className="text-sm font-semibold">{title}</h2>
      <p className="mt-0.5 text-xs text-ink-400">{hint}</p>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * The line under the community page's headline.
 *
 * HERE, AND ONLY HERE. It used to be settable only when a room was created,
 * so the 567 room carried "The 596 community." from its runbook with no way
 * for anybody running it to change or remove it. Text a community shows about
 * itself belongs to the people who run it.
 */
function About({ slug, saved }: { readonly slug: string; readonly saved: string }): ReactNode {
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = draft.trim() !== saved.trim();

  const save = (): void => {
    setBusy(true);
    setError(null);
    void communityAdmin.setDescription(slug, draft.trim()).then(
      () => { window.location.reload(); },
      (e: unknown) => {
        setBusy(false);
        setError(e instanceof ApiError ? e.message : 'Could not save that.');
      },
    );
  };

  return (
    <Panel title="Description" hint="Shown under the headline on the community page. Leave it empty to show nothing.">
      <label className="sr-only" htmlFor="community-description">Description</label>
      <textarea
        id="community-description"
        value={draft}
        onChange={(e) => { setDraft(e.target.value); }}
        maxLength={DESCRIPTION_MAX}
        rows={2}
        placeholder="No description"
        className="block w-full resize-y rounded-md border border-ink-800 bg-ink-950 px-3 py-2 text-sm placeholder:text-ink-700"
      />
      <div className="mt-1 text-right text-[11px] text-ink-700 tabular-nums">
        {draft.length}/{DESCRIPTION_MAX}
      </div>
      {error === null ? null : <p role="alert" className="mt-2 text-xs text-bad-400">{error}</p>}
      <div className="mt-2 flex items-center gap-2">
        <Button disabled={!dirty || busy} onClick={save}>
          {busy ? 'Saving…' : draft.trim() === '' && saved !== '' ? 'Remove description' : 'Save description'}
        </Button>
        {dirty ? (
          <button
            type="button"
            onClick={() => { setDraft(saved); setError(null); }}
            className="text-xs text-ink-400 hover:text-ink-50"
          >
            Undo
          </button>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * Listed or unlisted, changeable after the fact.
 *
 * It could only be chosen at creation here, so a community started private to
 * set up quietly had no way to go public once it was ready — the rooms app's
 * Manage dialog had the switch, and the page called "admin" did not. Absent
 * reads as public, the same default the server lists by.
 */
function Visibility({
  slug, saved,
}: {
  readonly slug: string;
  readonly saved: 'public' | 'private';
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const choose = (next: 'public' | 'private'): void => {
    if (next === saved || busy) return;
    setBusy(true);
    setError(null);
    void communityAdmin.setVisibility(slug, next).then(
      () => { window.location.reload(); },
      (e: unknown) => {
        setBusy(false);
        setError(e instanceof ApiError ? e.message : 'Could not save that.');
      },
    );
  };

  return (
    <Panel title="Visibility" hint="Who can find this community. Either way, anyone with the link can open it.">
      <div className="grid grid-cols-2 gap-2">
        {(['public', 'private'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => { choose(value); }}
            disabled={busy}
            aria-pressed={saved === value}
            className={`rounded-lg border px-3 py-2 text-left text-sm disabled:opacity-60 ${
              saved === value
                ? 'border-ink-50 bg-ink-950 text-ink-50'
                : 'border-ink-800 text-ink-400 hover:border-ink-700 hover:text-ink-50'
            }`}
          >
            {value === 'public' ? 'Public' : 'Private'}
            <span className="block text-[11px] text-ink-400">
              {/*
                Says what private actually MEANS. It is unlisted, not locked —
                somebody choosing it to keep strangers out would be choosing
                the wrong thing.
              */}
              {value === 'public' ? 'Listed on the front page' : 'Not listed. Anyone with the link'}
            </span>
          </button>
        ))}
      </div>
      {busy ? <p className="mt-2 text-xs text-ink-400">Saving…</p> : null}
      {error === null ? null : <p role="alert" className="mt-2 text-xs text-bad-400">{error}</p>}
    </Panel>
  );
}

/** How many channels the editor lists before "Show all". */
const LIST_STEP = 100;
/** How long a list gets before it is worth a box to search it. */
const FILTER_FROM = 20;

/** The contract's limit on a room description; the server refuses longer. */
const DESCRIPTION_MAX = 400;

/** Who is in the community. */
function Channels({
  slug, saved, fromFeed, feedLogins, grouping,
}: {
  readonly slug: string;
  readonly saved: readonly string[];
  readonly fromFeed: string | null;
  readonly feedLogins: readonly string[];
  /** The community's groupings, when loaded: each row shows and edits its own. */
  readonly grouping: GroupingEdit | null;
}): ReactNode {
  /** Whose row has its grouping picker open. One at a time. */
  const [picking, setPicking] = useState<string | null>(null);
  const savedSet = useMemo(() => new Set(saved), [saved]);
  const [draft, setDraft] = useState<readonly string[]>(saved);
  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** What the last pasted list did, which is news rather than an error. */
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);

  const addList = (raw: string): void => {
    const result = addLogins(draft, raw, ROOM_ROSTER_MAX);
    setDraft(result.logins);
    setEntry('');
    setError(null);
    setNotice(describeBulk(result, ROOM_ROSTER_MAX) || null);
  };

  const submit = (): void => {
    setNotice(null);
    if (looksLikeList(entry)) { addList(entry); return; }
    const result = addLogin(draft, entry, ROOM_ROSTER_MAX);
    setError(result.error);
    if (result.error === null) { setDraft(result.logins); setEntry(''); }
  };

  /*
   * The list, narrowed and cut. Five hundred rows is a list nobody reads
   * top to bottom: they look for a name. So a long roster gets a filter, and
   * the unfiltered view stops at LIST_STEP with the rest a click away —
   * rendering all of it on every keystroke of the add box buys nothing.
   */
  const needle = filter.trim().toLowerCase();
  const matching = useMemo(
    () => (needle.length === 0 ? draft : draft.filter((login) => login.includes(needle))),
    [draft, needle],
  );
  const listed = showAll || needle.length > 0 ? matching : matching.slice(0, LIST_STEP);

  const save = (action: 'save' | 'takeover'): void => {
    setBusy(true);
    setError(null);
    const call = action === 'takeover'
      ? communityAdmin.takeOverList(slug, [...feedLogins])
      : communityAdmin.setChannels(slug, [...draft]);
    void call.then(
      () => { window.location.reload(); },
      (e: unknown) => {
        setBusy(false);
        setError(e instanceof ApiError ? e.message : 'Could not save that.');
      },
    );
  };

  if (fromFeed !== null) {
    /*
     * A FEED-BACKED COMMUNITY CANNOT BE EDITED HERE, and saying so beats
     * offering a list that silently does nothing. Its roster is somebody
     * else's, and the room reads theirs in preference to ours — so an admin
     * carefully adding three people would see no change and have no idea why.
     */
    return (
      <Panel
        title="Channels"
        hint={`This community's roster comes from ${fromFeed}. It is not edited here.`}
      >
        <p className="text-sm text-ink-200">
          {feedLogins.length} channels, maintained by {fromFeed}.
        </p>
        <p className="mt-2 text-xs text-ink-400">
          Taking it over copies all {feedLogins.length} into this community&rsquo;s own list and
          stops reading theirs. Nothing is lost, and the list becomes yours to edit. It cannot be
          undone from here.
        </p>
        {error === null ? null : <p role="alert" className="mt-2 text-xs text-bad-400">{error}</p>}
        <div className="mt-3">
          <Button
            variant="ghost"
            disabled={busy || feedLogins.length === 0}
            onClick={() => { save('takeover'); }}
          >
            {busy ? 'Taking over…' : `Take over these ${String(feedLogins.length)} channels`}
          </Button>
        </div>
      </Panel>
    );
  }

  const dirty = hasChanges(saved, draft);
  return (
    <Panel
      title="Channels"
      hint={grouping && grouping.draft.groupings.length > 0
        ? 'Who belongs to this community, and which groupings each person is in. Grouping changes save as you make them.'
        : 'Who belongs to this community.'}
    >
      <div className="flex gap-2">
        <label className="sr-only" htmlFor="add-channel">Add a Twitch channel</label>
        <input
          id="add-channel"
          value={entry}
          onChange={(e) => { setEntry(e.target.value); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { submit(); e.preventDefault(); } }}
          /*
            A PASTED LIST IS ADDED WHOLE. A one-line input drops the line
            breaks from a pasted column — Chrome joins the names into one
            unreadable word — so the paste is read from the clipboard before
            the input can mangle it.
          */
          onPaste={(e) => {
            const pasted = e.clipboardData.getData('text');
            if (!looksLikeList(pasted)) return;
            e.preventDefault();
            addList(pasted);
          }}
          placeholder="twitch name, a link, or paste a whole list"
          className="min-w-0 flex-1 rounded-md border border-ink-800 bg-ink-950 px-3 py-2 text-sm placeholder:text-ink-700"
        />
        <Button variant="ghost" onClick={submit}>Add</Button>
      </div>
      {error === null ? null : <p role="alert" className="mt-2 text-xs text-bad-400">{error}</p>}
      {notice === null ? null : <p role="status" className="mt-2 text-xs text-ink-200">{notice}</p>}

      {draft.length === 0 ? (
        <Empty title="No channels yet." hint="Add somebody by their Twitch name, or paste a list." />
      ) : (
        <>
        <div className="mt-3 flex items-center gap-2">
          <p className="shrink-0 text-xs text-ink-400">
            {needle.length > 0
              ? `${String(matching.length)} of ${String(draft.length)}`
              : `${String(draft.length)} of ${String(ROOM_ROSTER_MAX)}`}
          </p>
          {draft.length > FILTER_FROM ? (
            <>
              <label className="sr-only" htmlFor="filter-channels">Find a channel in this list</label>
              <input
                id="filter-channels"
                value={filter}
                onChange={(e) => { setFilter(e.target.value); }}
                placeholder="find in this list"
                className="ml-auto w-44 min-w-0 rounded-md border border-ink-800 bg-ink-950 px-2.5 py-1 text-xs placeholder:text-ink-700"
              />
            </>
          ) : null}
        </div>
        <ul className="mt-1 divide-y divide-ink-800">
          {listed.map((login) => (
            <li key={login} className="py-1.5 text-sm">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="w-40 shrink-0 truncate">{login}</span>
                {grouping && grouping.draft.groupings.length > 0 ? (
                  savedSet.has(login) ? (
                    <RowGroupings
                      login={login}
                      edit={grouping}
                      open={picking === login}
                      onToggleOpen={() => { setPicking(picking === login ? null : login); }}
                    />
                  ) : (
                    // The server groups people on the saved roster only.
                    <span className="text-xs text-ink-700">Save to add to groupings</span>
                  )
                ) : null}
                <button
                  type="button"
                  onClick={() => { setDraft(removeLogin(draft, login)); }}
                  aria-label={`Remove ${login}`}
                  className="ml-auto rounded px-2 py-0.5 text-xs text-ink-400 hover:bg-ink-800 hover:text-bad-400"
                >
                  Remove
                </button>
              </div>
              {grouping && picking === login ? (
                <div className="mt-1.5 flex flex-wrap gap-1 pl-42">
                  {grouping.draft.groupings.map((g) => (
                    <Tick
                      key={g.id}
                      label={g.parent === null ? g.label : `${grouping.draft.groupings.find((p) => p.id === g.parent)?.label ?? ''} · ${g.label}`}
                      state={tickState(grouping.draft, login, g.id)}
                      onClick={() => { grouping.update(toggleTick(grouping.draft, login, g.id)); }}
                    />
                  ))}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {listed.length < matching.length ? (
          <button
            type="button"
            onClick={() => { setShowAll(true); }}
            className="mt-2 text-xs text-ink-400 hover:text-ink-50"
          >
            Show all {matching.length}
          </button>
        ) : null}
        </>
      )}

      <div className="mt-3 flex items-center gap-2">
        <Button disabled={!dirty || busy} onClick={() => { save('save'); }}>
          {busy ? 'Saving…' : 'Save changes'}
        </Button>
        {dirty ? (
          <button
            type="button"
            onClick={() => { setDraft(saved); setError(null); setNotice(null); }}
            className="text-xs text-ink-400 hover:text-ink-50"
          >
            Discard
          </button>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * The game this community is about (ADR 0052 §6).
 *
 * Twitch's own categories, searched on the server, so what is saved is an id
 * Twitch will report on every stream — typing "celeste" into a text box
 * would match nothing the day somebody spelled it differently.
 */
function Game({ slug, saved }: { readonly slug: string; readonly saved: readonly RoomGame[] }): ReactNode {
  const [draft, setDraft] = useState<readonly RoomGame[]>(saved);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<readonly RoomGame[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A search per pause in typing, not per keystroke; the last one wins.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setFound([]); return undefined; }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setSearching(true);
      void communityAdmin.searchGames(q, controller.signal).then(
        (games) => { setFound(games); setSearching(false); setError(null); },
        (e: unknown) => {
          if (controller.signal.aborted) return;
          setSearching(false);
          setError(e instanceof ApiError ? e.message : 'Could not search Twitch.');
        },
      );
    }, 300);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [query]);

  const chosen = new Set(draft.map((g) => g.id));
  const dirty = draft.map((g) => g.id).join(',') !== saved.map((g) => g.id).join(',');
  const full = draft.length >= ROOM_GAMES_MAX;

  const save = (): void => {
    setBusy(true);
    setError(null);
    void communityAdmin.setGames(slug, [...draft]).then(
      () => { window.location.reload(); },
      (e: unknown) => {
        setBusy(false);
        setError(e instanceof ApiError ? e.message : 'Could not save that.');
      },
    );
  };

  return (
    <Panel
      title="Game"
      hint="What this community plays. Streamers playing it are listed first when they are live. Leave it empty for a community about people rather than a game."
    >
      {draft.length > 0 ? (
        <ul className="mb-3 flex flex-wrap gap-1.5">
          {draft.map((game) => (
            <li key={game.id} className="flex items-center gap-1.5 rounded-full bg-ink-800 py-1 pr-1 pl-3 text-sm">
              {game.name}
              <button
                type="button"
                onClick={() => { setDraft(draft.filter((g) => g.id !== game.id)); }}
                aria-label={`Remove ${game.name}`}
                className="grid h-5 w-5 place-items-center rounded-full text-ink-400 hover:bg-ink-700 hover:text-ink-50"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <label className="sr-only" htmlFor="game-search">Search Twitch for a game</label>
      <input
        id="game-search"
        value={query}
        onChange={(e) => { setQuery(e.target.value); }}
        disabled={full}
        placeholder={full ? `That is ${String(ROOM_GAMES_MAX)} games already` : 'Search Twitch for a game'}
        className="w-full rounded-md border border-ink-800 bg-ink-950 px-3 py-2 text-sm placeholder:text-ink-700"
      />
      {searching ? <p className="mt-2 text-xs text-ink-400">Searching…</p> : null}
      {found.length > 0 && !full ? (
        <ul className="mt-1 divide-y divide-ink-800">
          {found.slice(0, 8).map((game) => (
            <li key={game.id} className="flex items-center gap-2 py-1.5 text-sm">
              {game.box_art_url ? (
                <img src={game.box_art_url.replace('{width}', '26').replace('{height}', '36')} alt="" width={26} height={36} className="shrink-0" />
              ) : null}
              <span className="truncate">{game.name}</span>
              <button
                type="button"
                disabled={chosen.has(game.id)}
                onClick={() => { setDraft([...draft, game]); setQuery(''); setFound([]); }}
                className="ml-auto rounded px-2 py-0.5 text-xs text-brand-300 hover:bg-ink-800 disabled:text-ink-700"
              >
                {chosen.has(game.id) ? 'Added' : 'Add'}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {error === null ? null : <p role="alert" className="mt-2 text-xs text-bad-400">{error}</p>}
      <div className="mt-3 flex items-center gap-2">
        <Button disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save game'}</Button>
        {dirty ? (
          <button type="button" onClick={() => { setDraft(saved); setError(null); }} className="text-xs text-ink-400 hover:text-ink-50">
            Undo
          </button>
        ) : null}
      </div>
    </Panel>
  );
}

/** The groupings draft, shared by the two panels that edit it. */
interface GroupingEdit {
  readonly draft: GroupingDraft;
  readonly update: (next: GroupingDraft) => void;
  readonly status: SaveStatus;
  /** The saved roster: the only people the server will group. */
  readonly roster: readonly string[];
}

type SaveStatus =
  | { readonly kind: 'idle' | 'saving' | 'saved' }
  | { readonly kind: 'error'; readonly message: string };

/** How long after the last change a save goes out. Long enough to finish typing a word. */
const AUTOSAVE_MS = 700;

/**
 * The groupings draft, SAVED AS IT CHANGES.
 *
 * Membership is edited in two places now — chips on each channel row, and
 * "Add people" on each grouping — and a Save button in one panel for changes
 * made in the other is a button somebody scrolls past. So every change goes
 * out on its own after a short pause, and both panels say whether it landed.
 *
 * A save that returns after the draft has moved on does not overwrite it:
 * the newer draft is still unsaved, and the effect sends it next.
 */
function useGroupingAutosave(slug: string, roster: readonly string[], initial: RoomGroupingsShape): GroupingEdit {
  const [saved, setSaved] = useState<GroupingDraft>(() => draftFrom(initial));
  const [draft, setDraft] = useState<GroupingDraft>(saved);
  const [status, setStatus] = useState<SaveStatus>({ kind: 'idle' });

  useEffect(() => {
    if (!groupingsChanged(draft, saved, roster)) return undefined;
    const why = unsavableBecause(draft);
    if (why !== null) { setStatus({ kind: 'error', message: why }); return undefined; }
    const timer = window.setTimeout(() => {
      setStatus({ kind: 'saving' });
      const sent = draft;
      void communityAdmin.saveGroupings(slug, groupingsBody(sent, roster)).then(
        (next) => {
          const fresh = draftFrom(next);
          setSaved(fresh);
          setDraft((current) => (current === sent ? fresh : current));
          setStatus({ kind: 'saved' });
        },
        (e: unknown) => {
          setStatus({ kind: 'error', message: e instanceof ApiError ? e.message : 'Could not save that.' });
        },
      );
    }, AUTOSAVE_MS);
    return () => { window.clearTimeout(timer); };
  }, [draft, saved, roster, slug]);

  return { draft, update: setDraft, status, roster };
}

function SaveNote({ status }: { readonly status: SaveStatus }): ReactNode {
  if (status.kind === 'idle') return null;
  if (status.kind === 'error') return <span role="alert" className="text-xs text-bad-400">{status.message}</span>;
  return (
    <span role="status" className="text-xs text-ink-400">
      {status.kind === 'saving' ? 'Saving…' : 'Saved'}
    </span>
  );
}

/**
 * The channel list and the groupings, which now edit one shared draft.
 *
 * Loaded separately from the rest of the page so a failure to read the
 * groupings leaves the channel list working, just without its chips.
 */
function Curation({
  slug, saved, fromFeed, feedLogins, groupings, retryGroupings,
}: {
  readonly slug: string;
  readonly saved: readonly string[];
  readonly fromFeed: string | null;
  readonly feedLogins: readonly string[];
  readonly groupings: Async<RoomGroupingsShape>;
  readonly retryGroupings: () => void;
}): ReactNode {
  // A feed's roster is not the room's, and the server groups the room's own list only.
  if (groupings.state !== 'ready' || fromFeed !== null) {
    return (
      <>
        {fromFeed === null ? (
          <Panel title="Groupings" hint="">
            {groupings.state === 'failed' ? (
              <Failed message="Could not load the groupings." detail={groupings.error.message} onRetry={retryGroupings} />
            ) : <Spinner label="Loading…" />}
          </Panel>
        ) : null}
        <Channels slug={slug} saved={saved} fromFeed={fromFeed} feedLogins={feedLogins} grouping={null} />
      </>
    );
  }
  return <CurationReady slug={slug} saved={saved} initial={groupings.value} />;
}

function CurationReady({
  slug, saved, initial,
}: {
  readonly slug: string;
  readonly saved: readonly string[];
  readonly initial: RoomGroupingsShape;
}): ReactNode {
  const edit = useGroupingAutosave(slug, saved, initial);
  return (
    <>
      <Groupings edit={edit} />
      <Channels slug={slug} saved={saved} fromFeed={null} feedLogins={[]} grouping={edit} />
    </>
  );
}

/** One person's groupings on their channel row: chips, and a way to change them. */
function RowGroupings({
  login, edit, open, onToggleOpen,
}: {
  readonly login: string;
  readonly edit: GroupingEdit;
  readonly open: boolean;
  readonly onToggleOpen: () => void;
}): ReactNode {
  const chips = chipsFor(edit.draft, login);
  return (
    <span className="flex flex-wrap items-center gap-1">
      {chips.map((chip) => (
        <span
          key={chip.id}
          title={chip.theirs ? 'They put themselves here. Only they can change it.' : undefined}
          className={`flex items-center gap-1 bg-ink-800 py-0.5 text-[11px] text-ink-200 ${chip.theirs ? 'px-2 ring-1 ring-ink-400' : 'pr-0.5 pl-2'}`}
        >
          {chip.label}
          {chip.theirs ? null : (
            <button
              type="button"
              onClick={() => { edit.update(toggleTick(edit.draft, login, chip.id)); }}
              aria-label={`Take ${login} out of ${chip.label}`}
              className="grid h-4 w-4 place-items-center text-ink-400 hover:text-bad-400"
            >
              ×
            </button>
          )}
        </span>
      ))}
      <button
        type="button"
        onClick={onToggleOpen}
        aria-expanded={open}
        className="px-1.5 py-0.5 text-[11px] text-brand-300 hover:bg-ink-800"
      >
        {open ? 'Done' : '+ Group'}
      </button>
    </span>
  );
}

/**
 * The community's own groupings (ADR 0052): what they are called, what they
 * mean, their order — and, per grouping, a way to add several people at once.
 * Who is in each is also edited on the channel rows below.
 */
function Groupings({ edit }: { readonly edit: GroupingEdit }): ReactNode {
  const { draft, update, roster } = edit;
  const [newLabel, setNewLabel] = useState('');
  const [newSection, setNewSection] = useState(false);
  /** The grouping whose "Add people" picker is open. */
  const [adding, setAdding] = useState<string | null>(null);
  /** The grouping whose removal is waiting for a second press. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const counts = new Map(draft.groupings.map((g) => {
    // A parent counts everybody in it or in one of its children, as the
    // community page does: being in "Speedrun · Vanilla" is being in "Speedrun".
    const ids = [g.id, ...draft.groupings.filter((c) => c.parent === g.id).map((c) => c.id)];
    const inside = (l: string) => ids.some((id) => { const t = tickState(draft, l, id); return t === 'on' || t === 'theirs-on'; });
    return [g.id, roster.filter(inside).length];
  }));

  const add = (): void => {
    update(addGrouping(draft, newLabel, { section: newSection }));
    setNewLabel('');
    setNewSection(false);
  };

  return (
    <Panel
      title="Groupings"
      hint="Your community's own categories. They become the filters and headings on the community page, and the notes are its legend. A grouping nobody is in is not shown to viewers. Changes save as you make them."
    >
      {draft.groupings.length === 0 ? (
        <p className="text-sm text-ink-400">No groupings yet. Add the first below.</p>
      ) : (
        <ol className="space-y-2">
          {draft.groupings.map((g) => (
            <li key={g.id} className={g.parent !== null ? 'pl-6' : ''}>
              <div className="flex flex-wrap items-start gap-2">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <input
                      aria-label="Name"
                      value={g.label}
                      maxLength={40}
                      onChange={(e) => { update(editGrouping(draft, g.id, { label: e.target.value })); }}
                      className="min-w-0 flex-1 rounded-md border border-ink-800 bg-ink-950 px-2.5 py-1 text-sm font-medium"
                    />
                    <span className="shrink-0 text-xs text-ink-400 tabular-nums" title="People in it">{counts.get(g.id) ?? 0}</span>
                    {g.section ? <span className="shrink-0 text-[11px] text-ink-400 uppercase">Section</span> : null}
                  </div>
                  <input
                    aria-label="What it means"
                    value={g.blurb}
                    maxLength={200}
                    placeholder="What it means, in a line"
                    onChange={(e) => { update(editGrouping(draft, g.id, { blurb: e.target.value })); }}
                    className="w-full rounded-md border border-ink-800 bg-ink-950 px-2.5 py-1 text-xs placeholder:text-ink-700"
                  />
                </div>
                <div className="flex shrink-0 items-center gap-0.5 pt-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => { setAdding(adding === g.id ? null : g.id); }}
                    aria-expanded={adding === g.id}
                    className="rounded px-1.5 py-0.5 text-brand-300 hover:bg-ink-800"
                  >
                    Add people
                  </button>
                  {g.parent === null ? (
                    <>
                      <IconButton label={`Move ${g.label} up`} onClick={() => { update(moveGrouping(draft, g.id, -1)); }}>↑</IconButton>
                      <IconButton label={`Move ${g.label} down`} onClick={() => { update(moveGrouping(draft, g.id, 1)); }}>↓</IconButton>
                    </>
                  ) : null}
                  {g.parent === null && !g.section ? (
                    <button
                      type="button"
                      onClick={() => { update(addGrouping(draft, 'New kind', { parent: g.id })); }}
                      title="Split this grouping, e.g. Speedrun into Vanilla and Modded"
                      className="rounded px-1.5 py-0.5 text-ink-400 hover:bg-ink-800 hover:text-ink-50"
                    >
                      + inside
                    </button>
                  ) : null}
                  {/*
                    TWO PRESSES, because it saves at once and takes everybody
                    out of it — and out of anything inside it.
                  */}
                  <button
                    type="button"
                    onClick={() => {
                      if (confirming === g.id || (counts.get(g.id) ?? 0) === 0) {
                        update(removeGrouping(draft, g.id));
                        setConfirming(null);
                      } else {
                        setConfirming(g.id);
                      }
                    }}
                    onBlur={() => { if (confirming === g.id) setConfirming(null); }}
                    className={`rounded px-1.5 py-0.5 hover:bg-ink-800 ${confirming === g.id ? 'text-bad-400' : 'text-ink-400 hover:text-bad-400'}`}
                  >
                    {confirming === g.id ? `Remove from ${String(counts.get(g.id) ?? 0)} people?` : 'Remove'}
                  </button>
                </div>
              </div>
              {adding === g.id ? (
                <AddPeople
                  roster={roster}
                  draft={draft}
                  groupingId={g.id}
                  onAdd={(logins) => { update(addPeople(draft, g.id, logins)); setAdding(null); }}
                  onCancel={() => { setAdding(null); }}
                />
              ) : null}
            </li>
          ))}
        </ol>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="new-grouping">New grouping</label>
        <input
          id="new-grouping"
          value={newLabel}
          maxLength={40}
          onChange={(e) => { setNewLabel(e.target.value); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { add(); e.preventDefault(); } }}
          placeholder="New grouping, e.g. Modded goldens"
          className="min-w-0 flex-1 rounded-md border border-ink-800 bg-ink-950 px-3 py-2 text-sm placeholder:text-ink-700"
        />
        <label className="flex items-center gap-1.5 text-xs text-ink-400" title="Its own block on the page, for organisations and event accounts rather than streamers">
          <input type="checkbox" checked={newSection} onChange={(e) => { setNewSection(e.target.checked); }} />
          Section
        </label>
        <Button variant="ghost" disabled={newLabel.trim().length === 0} onClick={add}>Add</Button>
      </div>
      <div className="mt-2 min-h-4"><SaveNote status={edit.status} /></div>
    </Panel>
  );
}

/**
 * Pick several people to add to one grouping.
 *
 * Only people who are not in it yet, and not people who took themselves out
 * of it: offering those would be offering a box that does not stay ticked.
 */
function AddPeople({
  roster, draft, groupingId, onAdd, onCancel,
}: {
  readonly roster: readonly string[];
  readonly draft: GroupingDraft;
  readonly groupingId: string;
  readonly onAdd: (logins: string[]) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const [filter, setFilter] = useState('');
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const candidates = roster.filter((l) => tickState(draft, l, groupingId) === 'off');
  const needle = filter.trim().toLowerCase();
  const shown = needle.length === 0 ? candidates : candidates.filter((l) => l.includes(needle));
  const flip = (login: string) => {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(login)) next.delete(login); else next.add(login);
      return next;
    });
  };

  return (
    <div className="mt-2 border border-ink-800 bg-ink-950 p-3">
      {candidates.length === 0 ? (
        <p className="text-xs text-ink-400">Everybody on the roster is in this already.</p>
      ) : (
        <>
          <input
            aria-label="Find people to add"
            value={filter}
            onChange={(e) => { setFilter(e.target.value); }}
            placeholder={`Find among ${String(candidates.length)} people`}
            autoFocus
            className="w-full rounded-md border border-ink-800 bg-ink-900 px-2.5 py-1 text-xs placeholder:text-ink-700"
          />
          <ul className="mt-2 grid max-h-64 grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-x-3 overflow-y-auto">
            {shown.map((login) => (
              <li key={login}>
                <label className="flex cursor-pointer items-center gap-2 py-0.5 text-sm hover:text-ink-50">
                  <input type="checkbox" checked={chosen.has(login)} onChange={() => { flip(login); }} />
                  <span className="truncate">{login}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="mt-2 flex items-center gap-2">
        <Button disabled={chosen.size === 0} onClick={() => { onAdd([...chosen]); }}>
          {chosen.size === 0 ? 'Add' : `Add ${String(chosen.size)}`}
        </Button>
        <button type="button" onClick={onCancel} className="text-xs text-ink-400 hover:text-ink-50">Cancel</button>
      </div>
    </div>
  );
}

function IconButton({ label, onClick, children }: { readonly label: string; readonly onClick: () => void; readonly children: ReactNode }): ReactNode {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="rounded px-1.5 py-0.5 text-ink-400 hover:bg-ink-800 hover:text-ink-50">
      {children}
    </button>
  );
}

/** One person × one grouping. The streamer's own choice is shown, not offered. */
function Tick({
  label, state, onClick,
}: {
  readonly label: string;
  readonly state: 'on' | 'off' | 'theirs-on' | 'theirs-off';
  readonly onClick: () => void;
}): ReactNode {
  const theirs = state === 'theirs-on' || state === 'theirs-off';
  const on = state === 'on' || state === 'theirs-on';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={theirs}
      aria-pressed={on}
      title={state === 'theirs-on'
        ? 'They put themselves here. Only they can change it.'
        : state === 'theirs-off'
          ? 'They took themselves out of this. Only they can change it.'
          : undefined}
      className={`rounded-full px-2 py-0.5 text-[11px] ${
        on ? 'bg-brand-500 font-semibold text-ink-950' : 'bg-ink-950 text-ink-400 hover:bg-ink-800 hover:text-ink-50'
      } ${theirs ? 'cursor-not-allowed ring-1 ring-ink-400' : ''} ${state === 'theirs-off' ? 'line-through' : ''}`}
    >
      {label}
    </button>
  );
}

/** Who runs it. The rows are already fetched — see the probe above. */
function Admins({
  slug, rows, onChanged,
}: {
  readonly slug: string;
  readonly rows: readonly RoomAdminShape[];
  readonly onChanged: () => void;
}): ReactNode {
  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const act = (run: Promise<unknown>): void => {
    setBusy(true);
    setError(null);
    void run.then(
      () => { setBusy(false); setEntry(''); onChanged(); },
      (e: unknown) => {
        setBusy(false);
        setError(e instanceof ApiError ? e.message : 'That did not work.');
      },
    );
  };

  return (
    <Panel title="Admins" hint="Who can change this community.">
      <div className="flex gap-2">
        <label className="sr-only" htmlFor="add-admin">Add an admin by Twitch name</label>
        <input
          id="add-admin"
          value={entry}
          onChange={(e) => { setEntry(e.target.value); }}
          placeholder="twitch name"
          className="min-w-0 flex-1 rounded-md border border-ink-800 bg-ink-950 px-3 py-2 text-sm placeholder:text-ink-700"
        />
        <Button
          variant="ghost"
          disabled={busy || entry.trim().length === 0}
          onClick={() => { act(communityAdmin.add(slug, entry.trim().toLowerCase(), 'editor')); }}
        >
          Add
        </Button>
      </div>
      {error === null ? null : <p role="alert" className="mt-2 text-xs text-bad-400">{error}</p>}

      <ul className="mt-3 divide-y divide-ink-800">
        {rows.map((row) => (
          <li
            key={`${row.handle ?? ''}-${row.uid ?? 'pending'}`}
            className="flex items-center gap-2 py-1.5 text-sm"
          >
            <span className="truncate">{row.handle ?? row.uid}</span>
            {row.owner ? <Tag>owner</Tag> : null}
            {/*
              An invitation nobody has collected is shown rather than hidden:
              a list of only people with accounts would hide exactly the grant
              somebody is waiting on.
            */}
            {row.pending ? <Tag>invited</Tag> : null}
            {row.owner ? null : (
              <button
                type="button"
                disabled={busy}
                onClick={() => { act(communityAdmin.remove(slug, row)); }}
                aria-label={`Remove ${row.handle ?? 'them'}`}
                className="ml-auto rounded px-2 py-0.5 text-xs text-ink-400 hover:bg-ink-800 hover:text-bad-400"
              >
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** Who is blocked from the community's chat. */
function Blocks({ slug }: { readonly slug: string }): ReactNode {
  const [list, retry] = useAsync((signal) => communityAdmin.blocks(slug, signal), [slug]);
  const [busy, setBusy] = useState(false);

  if (list.state === 'loading') return <Spinner label="Loading blocks…" />;
  if (list.state === 'failed') {
    return (
      <Panel title="Blocked" hint="People who cannot post in this community's chat.">
        <Failed message="Could not load the block list." detail={list.error.message} onRetry={retry} />
      </Panel>
    );
  }

  return (
    <Panel
      title="Blocked"
      hint="People who cannot post here. Blocking is done from the chat, where you can see what they said."
    >
      {list.value.length === 0 ? (
        <Empty title="Nobody is blocked." />
      ) : (
        <ul className="divide-y divide-ink-800">
          {list.value.map((mute: RoomMute) => (
            <li key={mute.uid} className="flex items-center gap-2 py-1.5 text-sm">
              <span className="truncate">{mute.handle ?? mute.uid}</span>
              {/*
                A timed mute and a block are the same row with different
                dates, so the list says which this is rather than showing a
                year-9999 timestamp nobody can read.
              */}
              <Tag>{isBlock(mute.until) ? 'blocked' : `until ${shortDate(mute.until)}`}</Tag>
              {mute.reason ? <span className="truncate text-xs text-ink-400">{mute.reason}</span> : null}
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void communityAdmin.unblock(slug, mute.uid).then(
                    () => { setBusy(false); retry(); },
                    () => { setBusy(false); },
                  );
                }}
                aria-label={`Unblock ${mute.handle ?? mute.uid}`}
                className="ml-auto rounded px-2 py-0.5 text-xs text-ink-400 hover:bg-ink-800 hover:text-ink-50"
              >
                Unblock
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Anything beyond a decade out is a block, not a mute somebody will outlast. */
function isBlock(until: string): boolean {
  return Date.parse(until) > Date.now() + 10 * 365 * 24 * 3600_000;
}

function shortDate(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? 'unknown' : at.toLocaleDateString();
}

function Tag({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <span className="shrink-0 rounded bg-ink-800 px-1.5 py-0.5 text-[11px] text-ink-200">
      {children}
    </span>
  );
}
